import { useState, useMemo, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Slider } from "@/components/ui/slider";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { useBranch } from "@/context/BranchContext";
import { rupiah, warungOpen } from "@/lib/format";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Truck, Store, Wallet, CreditCard, QrCode, ArrowLeft, Sparkles, Upload, FileImage, CheckCircle2, Loader2 } from "lucide-react";

export default function Checkout() {
  const { user, login } = useAuth();
  const { items, subtotal, clear } = useCart();
  const { branch, distanceKm } = useBranch();
  const navigate = useNavigate();
  const [customer_name, setName] = useState(user?.name || "");
  const [phone, setPhone] = useState("");
  const [address, setAddress] = useState("");
  const [notes, setNotes] = useState("");
  const [delivery_type, setDeliveryType] = useState("antar");
  const [delivery_slot, setSlot] = useState("prioritas");
  const [payment_method, setPayment] = useState("cod");
  const [redeem, setRedeem] = useState(0);
  const [loading, setLoading] = useState(false);
  const [designFiles, setDesignFiles] = useState({});
  const [uploading, setUploading] = useState({});
  const [referralName, setReferralName] = useState("");

  const designItems = items.filter((i) => i.needs_design);
  const hasFasnet = items.some((i) => i.category === "FASNET WiFi");
  const [isOpen, setIsOpen] = useState(warungOpen());

  useEffect(() => {
    const t = setInterval(() => setIsOpen(warungOpen()), 60000);
    return () => clearInterval(t);
  }, []);

  const uploadDesign = async (item, file) => {
    if (!file) return;
    setUploading((u) => ({ ...u, [item.key]: true }));
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads/design", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setDesignFiles((d) => ({ ...d, [item.key]: data }));
      toast.success(`Desain terupload untuk ${item.name}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal upload desain");
    } finally {
      setUploading((u) => ({ ...u, [item.key]: false }));
    }
  };

  const availablePoints = user?.poin_berkah || 0;
  const maxRedeem = Math.min(availablePoints, Math.floor(subtotal / 10));
  const points_discount = redeem * 10;
  const laundryKg = items.filter((i) => i.category === "FAS LAUNDRY").reduce((a, i) => a + i.qty, 0);
  const freePickup = laundryKg >= 10 && delivery_type === "antar";
  const withinRadius = distanceKm !== null && distanceKm <= 3;
  const shipping = delivery_type === "ambil" ? 0 : delivery_slot === "prioritas" ? 5000 : (withinRadius || subtotal >= 50000 || freePickup ? 0 : 3000);
  const packaging = delivery_type === "antar" ? 1000 : 0;
  const total = Math.max(0, subtotal + shipping + packaging - points_discount);
  const earn = Math.floor(subtotal / 1000);

  const submit = async () => {
    if (!user) return login();
    if (items.length === 0) return toast.error("Keranjang kosong");
    if (!customer_name || !phone || (delivery_type === "antar" && !address)) {
      return toast.error("Lengkapi nama, nomor HP, dan alamat pengiriman");
    }
    if (delivery_slot === "prioritas" && subtotal < 50000) return toast.error("Pengantaran Prioritas butuh minimal belanja Rp50.000");
    const missingDesign = designItems.filter((i) => !designFiles[i.key]);
    if (missingDesign.length > 0) {
      return toast.error(`Upload desain dulu untuk: ${missingDesign.map((i) => i.name).join(", ")}`);
    }
    setLoading(true);
    try {
      const payloadItems = items.map((it) => ({
        ...it,
        design_file_path: designFiles[it.key]?.path || "",
        design_file_name: designFiles[it.key]?.filename || "",
      }));
      const { data } = await api.post("/orders/checkout", {
        customer_name, phone, address, notes, delivery_slot, delivery_type, payment_method,
        branch_id: branch?.id || "", distance_km: distanceKm === null ? -1 : Math.round(distanceKm * 100) / 100,
        items: payloadItems, points_redeemed: redeem, referral_name: referralName,
      });
      clear();
      toast.success("Pesanan berhasil dibuat!", {
        description: data.payment_method === "transfer" && data.transfer_amount
          ? `Order ${data.id.slice(-6).toUpperCase()} — transfer TEPAT ${rupiah(data.transfer_amount)} ke BRI 7114 0102 0630 502 a.n. Abet Nigo`
          : `Order ${data.id.slice(-6).toUpperCase()}`,
      });
      navigate("/orders", { replace: true });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal membuat pesanan");
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen paper-bg">
        <Navbar />
        <div className="max-w-lg mx-auto text-center py-24 px-4">
          <div className="w-16 h-16 mx-auto rounded-2xl bg-emerald-700 text-white flex items-center justify-center text-2xl font-black">FS</div>
          <h2 className="mt-4 font-display font-black text-3xl">Masuk dulu untuk checkout</h2>
          <p className="mt-2 text-stone-600">Kami butuh info kamu untuk mengirim pesanan & mencatat Poin Member.</p>
          <Button data-testid="checkout-login-btn" onClick={login} className="mt-6 rounded-full bg-emerald-700 hover:bg-emerald-800 px-8 py-6 font-bold">Masuk dengan Google</Button>
        </div>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="min-h-screen paper-bg">
        <Navbar />
        <div className="max-w-lg mx-auto text-center py-24 px-4">
          <h2 className="font-display font-black text-3xl">Keranjang kosong</h2>
          <Link to="/" className="mt-4 inline-block text-emerald-700 font-bold">← Kembali belanja</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen paper-bg">
      <Navbar />
      <div className="max-w-6xl mx-auto px-4 md:px-8 py-8">
        <Link to="/" data-testid="back-to-home-btn" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-emerald-700 text-white text-sm font-bold shadow-md shadow-emerald-700/25 hover:bg-emerald-800 hover:shadow-lg hover:-translate-y-0.5 transition mb-4">
          <ArrowLeft className="w-4 h-4" /> Kembali Belanja
        </Link>
        <h1 className="font-display font-black text-4xl md:text-5xl tracking-tight">Checkout Warung</h1>
        <p className="text-stone-600 mt-1">Isi info pengiriman & pilih cara bayar.</p>

        <div className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-white rounded-3xl border border-[#E5DECF] p-6">
              <h3 className="font-display font-bold text-lg mb-4">Info Pengiriman</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="cn">Nama Penerima</Label>
                  <Input data-testid="checkout-name-input" id="cn" value={customer_name} onChange={(e) => setName(e.target.value)} className="mt-1" />
                </div>
                <div>
                  <Label htmlFor="ph">Nomor HP / WA</Label>
                  <Input data-testid="checkout-phone-input" id="ph" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="0812xxxxxxxx" className="mt-1" />
                </div>
                <div className="sm:col-span-2">
                  <Label htmlFor="ad">Alamat Lengkap</Label>
                  <Textarea data-testid="checkout-address-input" id="ad" value={address} onChange={(e) => setAddress(e.target.value)} placeholder="Jalan, RT/RW, patokan..." className="mt-1" rows={3} />
                </div>
                <div className="sm:col-span-2">
                  <Label htmlFor="nt">Catatan untuk Kurir</Label>
                  <Input data-testid="checkout-notes-input" id="nt" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Pagar hitam, titip di teras" className="mt-1" />
                </div>
                <div className="sm:col-span-2">
                  <Label>Pilih Jam Pengantaran</Label>
                  <Select value={delivery_slot} onValueChange={setSlot}>
                    <SelectTrigger data-testid="checkout-delivery-slot-select" className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="prioritas" disabled={subtotal < 50000} data-testid="delivery-slot-prioritas">
                        Prioritas 30–60 mnt{subtotal < 50000 ? " — min. belanja Rp50.000" : " (ongkir Rp5.000)"}
                      </SelectItem>
                      {(() => {
                        const nowWib = new Date(new Date().toLocaleString("en-US", { timeZone: "Asia/Jakarta" }));
                        const nowMin = nowWib.getHours() * 60 + nowWib.getMinutes();
                        const SLOTS = [
                          { s: "08.30–10.00", cut: "08.15" },
                          { s: "10.30–12.00", cut: "10.15" },
                          { s: "13.30–15.00", cut: "13.15" },
                          { s: "15.30–17.00", cut: "15.15" },
                          { s: "18.00–19.30", cut: "17.45" },
                        ];
                        return (
                          <>
                            {SLOTS.map(({ s, cut }) => {
                              const [ch, cm] = cut.split(".").map(Number);
                              const passed = ch * 60 + cm <= nowMin;
                              return (
                                <SelectItem key={s} value={`Reguler ${s}`} disabled={passed} data-testid={`delivery-slot-${s.slice(0, 5).replace(".", "")}`}>
                                  Reguler {s}{passed ? ` — cut-off ${cut} sudah lewat` : ` (order sblm ${cut})`}
                                </SelectItem>
                              );
                            })}
                            {nowMin > 17 * 60 + 45 && (
                              <SelectItem value="Besok Pagi 08.30–10.00" data-testid="delivery-slot-besok-pagi">
                                🌅 Besok Pagi 08.30–10.00 (cut-off hari ini sudah lewat)
                              </SelectItem>
                            )}
                          </>
                        );
                      })()}
                    </SelectContent>
                  </Select>
                  <p data-testid="delivery-slot-note" className="mt-2 text-xs text-stone-500 leading-relaxed">
                    Order reguler ditutup <b>15 menit sebelum</b> jam pengantaran (cut-off). Slot terakhir <b>18.00–19.30</b> (cut-off <b>17.45</b>); lewat itu otomatis muncul opsi <b>Besok Pagi 08.30–10.00</b> atau ambil sendiri di toko. <b>Prioritas 30–60 mnt</b>: min. belanja Rp50.000 + ongkir Rp5.000. Reguler: gratis ongkir min. belanja Rp50.000.
                  </p>
                </div>
                <div>
                  <Label>Metode Pengiriman</Label>
                  <RadioGroup value={delivery_type} onValueChange={setDeliveryType} className="mt-1 grid grid-cols-2 gap-2">
                    <label data-testid="delivery-antar" className={`cursor-pointer border rounded-xl p-3 flex items-center gap-2 ${delivery_type === "antar" ? "border-emerald-700 bg-emerald-50" : "border-[#E5DECF]"}`}>
                      <RadioGroupItem value="antar" className="sr-only" />
                      <Truck className="w-4 h-4 text-emerald-700" /> <span className="text-sm font-semibold">Antar</span>
                    </label>
                    <label data-testid="delivery-ambil" className={`cursor-pointer border rounded-xl p-3 flex items-center gap-2 ${delivery_type === "ambil" ? "border-emerald-700 bg-emerald-50" : "border-[#E5DECF]"}`}>
                      <RadioGroupItem value="ambil" className="sr-only" />
                      <Store className="w-4 h-4 text-emerald-700" /> <span className="text-sm font-semibold">Ambil di Warung</span>
                    </label>
                  </RadioGroup>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-3xl border border-[#E5DECF] p-6">
              <h3 className="font-display font-bold text-lg mb-4">Metode Pembayaran</h3>
              <RadioGroup value={payment_method} onValueChange={setPayment} className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {[
                  { v: "cod", label: "Bayar di Tempat", icon: Wallet, testid: "pay-cod" },
                  { v: "transfer", label: "Transfer Bank", icon: CreditCard, testid: "pay-transfer" },
                  { v: "qris", label: "QRIS (segera)", icon: QrCode, testid: "pay-qris" },
                ].map((m) => (
                  <label
                    key={m.v}
                    data-testid={m.testid}
                    className={`cursor-pointer rounded-xl border p-4 flex flex-col gap-1 ${payment_method === m.v ? "border-emerald-700 bg-emerald-50" : "border-[#E5DECF]"}`}
                  >
                    <RadioGroupItem value={m.v} className="sr-only" />
                    <m.icon className="w-5 h-5 text-emerald-700" />
                    <span className="text-sm font-semibold">{m.label}</span>
                  </label>
                ))}
              </RadioGroup>
              {payment_method === "transfer" && (
                <div data-testid="checkout-transfer-info" className="mt-4 rounded-2xl bg-sky-50 border border-sky-200 p-4">
                  <div className="text-xs font-bold text-sky-900 uppercase tracking-widest">Transfer ke rekening</div>
                  <div className="mt-1 font-mono font-black text-lg text-sky-800">Bank BRI · 7114 0102 0630 502</div>
                  <div className="text-xs font-semibold text-sky-700">a.n. Abet Nigo</div>
                  <p className="mt-2 text-xs text-stone-600 leading-relaxed">Nominal transfer akan <b>ditambah 3 angka unik di belakang</b> (mis. total Rp67.000 → transfer Rp67.045) agar admin mudah cek mutasi. Nominal pasti + kode unik muncul setelah pesanan dibuat, di halaman <b>Pesanan Saya</b>.</p>
                </div>
              )}
            </div>

            {hasFasnet && (
              <div className="bg-gradient-to-br from-blue-50 to-orange-50 rounded-3xl border border-blue-200 p-6" data-testid="checkout-fasnet-referral-section">
                <h3 className="font-display font-bold text-lg flex items-center gap-2">📡 Referral FASNET WiFi</h3>
                <p className="text-xs text-stone-600 mt-1">Ada tetangga yang mengajak pasang FASNET? Cantumkan namanya agar dia dapat 🎁 ½ kg gula pasir + 5 bungkus Indomie kaldu.</p>
                <Input
                  data-testid="checkout-referral-input"
                  value={referralName}
                  onChange={(e) => setReferralName(e.target.value)}
                  placeholder="Nama tetangga pengajak (opsional)"
                  className="mt-3 bg-white"
                />
              </div>
            )}

            {designItems.length > 0 && (
              <div className="bg-white rounded-3xl border border-[#E5DECF] p-6" data-testid="checkout-design-upload-section">
                <div className="flex items-center gap-2">
                  <Upload className="w-5 h-5 text-emerald-700" />
                  <h3 className="font-display font-bold text-lg">Upload Desain Cetak</h3>
                </div>
                <p className="text-xs text-stone-500 mt-1">Wajib untuk produk FAS Printing. Format JPG/PNG/WEBP/PDF, maks 10MB.</p>
                <div className="mt-4 space-y-3">
                  {designItems.map((it) => {
                    const up = designFiles[it.key];
                    const busy = uploading[it.key];
                    return (
                      <div key={it.key} data-testid={`design-upload-${it.product_id}`} className="flex items-center gap-3 rounded-2xl border border-[#E5DECF] p-3">
                        <img src={it.image_url} alt="" className="w-12 h-12 rounded-xl object-cover" />
                        <div className="flex-1 min-w-0">
                          <div className="font-bold text-sm truncate">{it.name}{it.variant_name ? ` · ${it.variant_name}` : ""}</div>
                          {up ? (
                            <div className="flex items-center gap-1 text-xs text-emerald-700 font-semibold mt-0.5">
                              <CheckCircle2 className="w-3.5 h-3.5" /> {up.filename}
                            </div>
                          ) : (
                            <div className="text-xs text-stone-500 mt-0.5">Belum ada file desain</div>
                          )}
                        </div>
                        <label
                          data-testid={`design-upload-btn-${it.product_id}`}
                          className={`shrink-0 cursor-pointer px-3 py-2 rounded-full text-xs font-bold flex items-center gap-1 transition ${
                            up ? "bg-stone-100 text-stone-700 hover:bg-stone-200" : "bg-emerald-700 text-white hover:bg-emerald-800"
                          }`}
                        >
                          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileImage className="w-3.5 h-3.5" />}
                          {busy ? "Mengupload..." : up ? "Ganti" : "Upload"}
                          <input
                            type="file"
                            accept=".jpg,.jpeg,.png,.webp,.pdf"
                            className="hidden"
                            onChange={(e) => uploadDesign(it, e.target.files?.[0])}
                          />
                        </label>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {availablePoints > 0 && (
              <div className="bg-gradient-to-br from-amber-100 to-orange-100 rounded-3xl border border-amber-200 p-6">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-orange-600" />
                  <h3 className="font-display font-bold text-lg">Tukar Poin Member</h3>
                </div>
                <p className="text-sm text-stone-700 mt-1">Kamu punya <b>{availablePoints} poin</b>. 100 poin = Rp 1.000 diskon.</p>
                <div className="mt-4 flex items-center gap-4">
                  <Slider
                    data-testid="checkout-points-slider"
                    value={[redeem]}
                    onValueChange={(v) => setRedeem(v[0])}
                    max={maxRedeem}
                    step={100}
                    className="flex-1"
                  />
                  <div className="text-right">
                    <div className="font-display font-black text-2xl text-orange-700" data-testid="checkout-points-value">{redeem}</div>
                    <div className="text-[10px] font-mono uppercase tracking-widest text-stone-600">− {rupiah(redeem * 10)}</div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-3xl border border-[#E5DECF] p-6 sticky top-24">
              <h3 className="font-display font-bold text-lg mb-3">Ringkasan Pesanan</h3>
              <div className="space-y-2 max-h-56 overflow-y-auto pr-1">
                {items.map((it) => (
                  <div key={it.key} className="flex gap-2 items-center text-sm">
                    <img src={it.image_url} alt="" className="w-10 h-10 rounded-lg object-cover" />
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold truncate">{it.name}</div>
                      <div className="text-xs text-stone-500">{it.qty} × {rupiah(it.price)}{it.variant_name ? ` · ${it.variant_name}` : ""}</div>
                    </div>
                    <div className="font-bold text-sm">{rupiah(it.qty * it.price)}</div>
                  </div>
                ))}
              </div>
              <hr className="my-4 border-[#E5DECF]" />
              <div className="space-y-1 text-sm">
                <Row label="Subtotal" value={rupiah(subtotal)} testid="sum-subtotal" />
                <Row label={`Ongkir ${shipping === 0 ? '(gratis)' : ''}`} value={rupiah(shipping)} testid="sum-ship" />
                {branch && (
                  <div data-testid="checkout-branch-info" className={`mt-1 rounded-xl border px-3 py-2 text-xs font-semibold flex items-center gap-1.5 ${withinRadius || delivery_type === "ambil" || distanceKm === null ? "bg-emerald-50 border-emerald-200 text-emerald-700" : "bg-amber-50 border-amber-300 text-amber-800"}`}>
                    <Truck className="w-3.5 h-3.5" />
                    <span>
                      Cabang: {branch.name}{distanceKm !== null ? ` · jarak ${distanceKm.toFixed(1)} km` : " · pilih/deteksi lokasi untuk hitung jarak"}
                      {delivery_type === "antar" && distanceKm !== null && !withinRadius && " — di luar zona gratis 3 KM, ongkir berlaku"}
                    </span>
                  </div>
                )}
                {freePickup && (
                  <div data-testid="laundry-free-pickup-badge" className="mt-1 rounded-xl bg-emerald-50 border border-emerald-200 px-3 py-2 text-xs font-bold text-emerald-700 flex items-center gap-1.5">
                    <Truck className="w-3.5 h-3.5" /> Gratis Antar Jemput — laundry {laundryKg} kg ≥ 10 kg
                  </div>
                )}
                <Row label="Kemasan ramah lingkungan" value={rupiah(packaging)} testid="sum-pack" />
                {points_discount > 0 && <Row label="Diskon Poin" value={`− ${rupiah(points_discount)}`} testid="sum-disc" className="text-orange-600" />}
              </div>
              <hr className="my-4 border-[#E5DECF]" />
              <div className="flex items-baseline justify-between">
                <span className="font-display font-bold">Total</span>
                <span data-testid="sum-total" className="font-display font-black text-2xl text-emerald-800">{rupiah(total)}</span>
              </div>
              <div className="mt-2 text-[11px] font-mono uppercase tracking-widest text-emerald-700">
                <Sparkles className="w-3 h-3 inline mr-1" /> Kamu akan dapat +{earn} poin
              </div>
              <Button
                data-testid="checkout-place-order-btn"
                onClick={submit}
                disabled={loading || !isOpen}
                className="w-full mt-4 rounded-full bg-emerald-700 hover:bg-emerald-800 text-white font-bold py-6 text-base disabled:bg-stone-300 disabled:text-stone-500"
              >
                {loading ? "Memproses..." : isOpen ? "Buat Pesanan" : "Warung Tutup"}
              </Button>
              {!isOpen && (
                <div data-testid="checkout-closed-notice" className="mt-3 rounded-xl bg-rose-50 border border-rose-200 px-4 py-3 text-sm text-rose-700 font-semibold text-center">
                  🕒 Warung tutup, buka lagi 06.00 WIB
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value, className = "", testid }) {
  return (
    <div className={`flex justify-between ${className}`}>
      <span className="text-stone-600">{label}</span>
      <span data-testid={testid} className="font-semibold">{value}</span>
    </div>
  );
}
